/**
 * Apple Ads：目标费率与成本配置 API 服务（CLAUDE.md §前端 API 服务层 — 按域分目录）。
 *
 * 与后端 Pydantic 模型 apple_asa.dtos.feilv_target_config 对齐。
 *   - GET /api/v1/apple-asa/feilv-target-config  → 读配置当前态（任意登录用户可读；
 *     表无行 / DDL 未执行时后端 fail-open 返回默认态 custom + 0.01 + 成本 4000）
 *   - PUT /api/v1/apple-asa/feilv-target-config  → 保存配置（仅 v_summerxiao /
 *     reizhu / v_hooverhuang / admin / devinzhang，否则后端 403；模式/取值不合法
 *     后端 400；写库失败 500）
 *
 * 与腾讯域 services/tencent/feilvTargetConfig.ts 的差异（PR-AT-20260921-005）：
 *   - 无 sxfl_per_3d / sxfl_per_7d 回显字段（apple 侧无对应 statis 数据源）；
 *   - 当前仅支持 custom 模式（sxfl_3d/sxfl_7d 为 DDL 预留枚举，未启用）；
 *   - 含 cost_value 目标企业进件成本（元，PR-AT-20260923-012 新增，
 *     与盯盘指标 cost_ent_appl 同口径）。
 */

import { request } from "../apiClient";

/**
 * 目标费率取值模式（与后端 FEILV_MODE_* 常量对齐）。
 * sxfl_3d/sxfl_7d 为 DDL CHECK 预留枚举（跟随链路平均费率），当前未启用——
 * 保存仅接受 custom；Record 侧保留联合类型以如实承载库内可能的预留值。
 */
export type FeilvMode = "custom" | "sxfl_3d" | "sxfl_7d";

/** 目标费率与成本配置当前态（与后端 FeilvTargetConfigRecord 对齐；None 字段经 exclude_none 不下发）。 */
export interface FeilvTargetConfigRecord {
  config_key: string;
  feilv_mode: FeilvMode;
  /** custom 模式固定值（小数费率，如 0.01=1%）。 */
  feilv_value: number;
  /** 目标企业进件成本（元，与盯盘指标 cost_ent_appl 同口径，默认 4000）。 */
  cost_value: number;
  create_user?: string;
  modify_user?: string;
  created_time?: string;
  updated_time?: string;
}

export interface SaveFeilvTargetConfigBody {
  /** 当前仅接受 "custom"（后端 service 层校验，其余值 400）。 */
  feilv_mode: "custom";
  /** custom 模式固定值（(0,1) 开区间小数费率）。 */
  feilv_value: number;
  /** 目标企业进件成本（元，(0,20000) 开区间）。 */
  cost_value: number;
}

/** 读取目标费率与成本配置当前态；表无行 / DDL 未执行时后端返回默认态 custom + 0.01 + 成本 4000。 */
export async function getFeilvTargetConfig(): Promise<FeilvTargetConfigRecord | null> {
  return request<FeilvTargetConfigRecord | null>(
    "/api/v1/apple-asa/feilv-target-config",
  );
}

/** 保存目标费率与成本配置；返回保存后当前态（含更新后的审计列）。非白名单用户后端返回 403。 */
export async function saveFeilvTargetConfig(
  body: SaveFeilvTargetConfigBody,
): Promise<FeilvTargetConfigRecord> {
  return request<FeilvTargetConfigRecord>(
    "/api/v1/apple-asa/feilv-target-config",
    {
      method: "PUT",
      body,
    },
  );
}
